<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import Layout from '../components/layout/Layout.vue'
import StellarBackground from '../components/animations/StellarBackground.vue'
import Button from '../components/ui/Button.vue'
import { h, computed } from 'vue'

const BlueskyIcon = () => h('svg', {
  xmlns: 'http://www.w3.org/2000/svg',
  width: '20',
  height: '20',
  viewBox: '0 0 512 512',
  style: 'margin-right: 8px;',
}, [
  h('path', {
    fill: 'currentColor',
    d: 'M111.8 62.2C170.2 105.9 233 194.7 256 242.4c23-47.6 85.8-136.4 144.2-180.2c42.1-31.6 110.3-56 110.3 21.8c0 15.5-8.9 130.5-14.1 149.2C478.2 298 412 314.6 353.1 304.5c102.9 17.5 129.1 75.5 72.5 133.5c-107.4 110.2-154.3-27.6-166.3-62.9l0 0c-1.7-4.9-2.6-7.8-3.3-7.8s-1.6 3-3.3 7.8l0 0c-12 35.3-59 173.1-166.3 62.9c-56.5-58-30.4-116 72.5-133.5C100 314.6 33.8 298 15.7 233.1C10.4 214.4 1.5 99.4 1.5 83.9c0-77.8 68.2-53.4 110.3-21.8z'
  })
])

const TiktokIcon = () => h('svg', {
  xmlns: 'http://www.w3.org/2000/svg',
  width: '24',
  height: '24',
  viewBox: '0 0 24 24',
  style: 'margin-right: 2px;',
}, [
  h('path', {
    fill: 'currentColor',
    d: 'M16.6 5.82s.51.5 0 0A4.278 4.278 0 0 1 15.54 3h-3.09v12.4a2.592 2.592 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6c0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64c0 3.33 2.76 5.7 5.69 5.7c3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48z'
  })
])

const { t, tm } = useI18n()

const discordInviteLink = "https://alterlan.be/discord"

const discordBenefits = computed(() => tm('contact.discord.benefits'))

const eventContactInfo = computed(() => tm('contact.event.info'))

const socialLinks = [
  { 
    icon: BlueskyIcon,
    name: t('contact.social.platforms.bluesky'),
    link: 'https://bsky.app/profile/alterlan.be',
    isSvg: true
  },
  { icon: 'mdi-twitch', name: t('contact.social.platforms.twitch'), link: 'https://twitch.tv/alterlan_' },
  { icon: 'mdi-instagram', name: t('contact.social.platforms.instagram'), link: 'https://instagram.com/alterlan_' },
  { icon: 'mdi-youtube', name: t('contact.social.platforms.youtube'), link: 'https://www.youtube.com/@ALTERLAN_BE?sub_confirmation=1' },
  { 
    icon: TiktokIcon,
    name: t('contact.social.platforms.tiktok'),
    link: 'https://www.tiktok.com/@alterlan',
    isSvg: true
  },
  { icon: 'mdi-steam', name: t('contact.social.platforms.steam'), link: 'https://steamcommunity.com/groups/alterlan' }
]
</script>

<template>
  <Layout>
    <div class="contact-wrapper">
      <StellarBackground backColor="#2d3434" :circle-offset="{ x: 350, y: 100 }" />
      <div class="contact-container">
        <div class="contact-page" :class="{ 'content-loaded': true }">
          <!-- Header Section -->
          <div class="contact-header">
            <h1>{{ t('contact.title') }}</h1>
            <p>{{ t('contact.subtitle') }}</p>
          </div>

          <!-- Main Content -->
          <div class="content-section">
            <v-row>
              <!-- Discord Contact -->
              <v-col cols="12" md="6">
                <div class="section-container">
                  <h2 class="section-title">
                    <v-icon class="section-icon" size="32" color="#76c4ae">mdi-message-text</v-icon>
                    {{ t('contact.discord.title') }}
                  </h2>
                  <v-card class="content-card" elevation="0">
                    <v-card-text>
                      <div class="mb-4 w-100">
                        <h3 class="text-h6 mb-6">{{ t('contact.discord.description') }}</h3>
                        <div class="contact-list d-flex justify-center align-center">
                          <div v-for="(benefit, index) in discordBenefits" :key="index" class="contact-item">
                            <v-icon color="#df824d" class="mr-2">mdi-check</v-icon>
                            <span>{{ benefit }}</span>
                          </div>
                        </div>
                        <Button class="mt-4" :href="discordInviteLink" target="_blank" rel="noopener noreferrer"
                          size="x-large">
                          {{ t('contact.discord.joinButton') }}
                        </Button>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>

              <!-- Event Contact -->
              <v-col cols="12" md="6">
                <div class="section-container">
                  <h2 class="section-title">
                    <v-icon class="section-icon" size="32" color="#76c4ae">mdi-account-group</v-icon>
                    {{ t('contact.event.title') }}
                  </h2>
                  <v-card class="content-card" elevation="0">
                    <v-card-text>
                      <div class="mb-4">
                        <h3 class="text-h6 mb-6">{{ t('contact.event.description') }}</h3>
                        <div class="contact-list d-flex justify-center align-center">
                          <div v-for="(info, index) in eventContactInfo" :key="index" class="contact-item">
                            <v-icon color="#df824d" class="mr-2">mdi-check</v-icon>
                            <span>{{ info }}</span>
                          </div>
                        </div>
                        <p class="mt-4 mb-7 text-center">{{ t('contact.event.location') }}</p>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>
            </v-row>

            <!-- Social Media and Email Section -->
            <v-row class="mt-2">
              <!-- Support Email -->
              <v-col cols="12" md="3">
                <div class="section-container">
                  <h2 class="section-title">
                    <v-icon class="section-icon" size="32" color="#76c4ae">mdi-help-circle</v-icon>
                    {{ t('contact.support.title') }}
                  </h2>
                  <v-card class="content-card" elevation="0">
                    <v-card-text>
                      <div class="mb-4 text-center">
                        <h3 class="text-h6 mb-2">{{ t('contact.support.description') }}</h3>
                        <Button class="mt-4" href='mailto:support@alterlan.be' target="_blank" rel="noopener noreferrer" icon-start="mdi-email" size="large">
                          {{ t('contact.support.button') }}
                        </Button>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>

              <!-- Social Media Links -->
              <v-col cols="12" md="6">
                <div class="section-container">
                  <h2 class="section-title">
                    <v-icon class="section-icon" size="32" color="#76c4ae">mdi-share-variant</v-icon>
                    {{ t('contact.social.title') }}
                  </h2>
                  <v-card class="content-card" elevation="0">
                    <v-card-text>
                      <div class="mb-4 text-center">
                        <h3 class="text-h6 mb-2">{{ t('contact.social.description') }}</h3>
                        <div class="social-grid">
                          <Button v-for="social in socialLinks" :key="social.name" :href="social.link" target="_blank"
                            rel="noopener noreferrer" :icon-start="social.isSvg ? undefined : social.icon"
                            class="social-button" size="large">
                            <template v-if="social.isSvg">
                              <component :is="social.icon" />
                            </template>
                            {{ social.name }}
                          </Button>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>

              <!-- Crew Email -->
              <v-col cols="12" md="3">
                <div class="section-container">
                  <h2 class="section-title">
                    <v-icon class="section-icon" size="32" color="#76c4ae">mdi-account-group</v-icon>
                    {{ t('contact.crew.title') }}
                  </h2>
                  <v-card class="content-card" elevation="0">
                    <v-card-text>
                      <div class="mb-4 text-center">
                        <h3 class="text-h6 mb-2">{{ t('contact.crew.description') }}</h3>
                        <Button class="mt-4" href="mailto:crew@alterlan.be" target="_blank" rel="noopener noreferrer" icon-start="mdi-email" size="large">
                          {{ t('contact.crew.button') }}
                        </Button>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>
            </v-row>
          </div>
          <div class="bottom-links d-none d-md-flex">
            <router-link to="/privacy" class="nav-link" v-html="t('menu.footerLinks.privacy')"></router-link>
            <a href="https://alterlan.be/terms" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.terms')"></a>
            <router-link to="/contact" class="nav-link" v-html="t('menu.footerLinks.contact')"></router-link>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.contact-wrapper {
  position: relative;
  min-height: calc(100vh - 80px);
  width: 100%;
  overflow-y: hidden;
  scroll-behavior: smooth;
}

:deep(.stellar-background) {
  position: fixed;
  top: 80px;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.contact-container {
  min-width: 100vw !important;
  z-index: 1;
  overflow-x: hidden;
}

.contact-page {
  position: relative;
  padding: 2rem;
  min-height: calc(100vh - 80px);
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;
}

.content-loaded {
  opacity: 1;
  transform: translateY(0);
}

.contact-header {
  text-align: center;
  position: relative;
  margin-bottom: 3rem;
  margin-top: 3rem;
}

.contact-header h1 {
  font-family: 'Airborne GP', sans-serif;
  font-size: 3.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #76c4ae;
  letter-spacing: 4px;
  position: relative;
  display: inline-block;
}

.contact-header p {
  font-size: 1.2rem;
  color: #ccc;
  margin: 1rem auto 0;
  line-height: 1.6;
  position: relative;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.content-section {
  width: 100%;
  max-width: 100vw;
  margin: 0 auto;
  padding: 2rem;
}

.section-container {
  margin-bottom: 4rem;
  width: 100%;
}

.section-title {
  font-size: 1.8rem;
  font-weight: 600;
  color: white;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  text-align: center;
}

.section-icon {
  margin-right: 0.5rem;
}

.content-card {
  background: rgba(0, 0, 0, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  box-shadow: #76c4ae 0 0, #76c4ae80 0 0;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  max-width: 100%;
  margin: 1rem 0;
  height: 100%;
  min-height: 350px;
  display: flex;
  flex-direction: column;
}

.content-card:hover {
  box-shadow: rgba(118, 196, 174, 0.3) -10px 10px, rgba(118, 196, 174, 0.2) -20px 20px !important;
}

.content-card :deep(.v-card-text) {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.contact-list {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex-grow: 1;
}

.contact-item {
  display: flex;
  align-items: center;
  color: #FFF;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.social-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-top: 1rem;
  width: 100%;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.social-button {
  width: 100%;
  justify-content: center;
}

.custom-svg-icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

.bottom-links {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 4rem;
  padding: 1rem 0 1rem 0;
}

.nav-link {
  color: white;
  min-width: 100px;
  text-align: center;
  text-decoration: none;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #e69b71;
}

@media (max-width: 768px) {
  .bottom-links {
    gap: 2rem;
  }

  .nav-link {
    font-size: 0.7rem;
  }
}

.custom-svg-icon :deep(svg) {
  width: 100%;
  height: 100%;
}

@media (max-width: 2040px) {
  .content-card {
    min-height: 350px;
  }
}

@media (max-width: 960px) {
  .contact-header h1 {
    font-size: 2.5rem;
  }

  .contact-header p {
    font-size: 1rem;
  }

  .content-section {
    padding: 1rem;
  }

  .content-card {
    margin: 0.5rem 0;
    padding: 1rem;
    width: 100% !important;
  }

  .section-container {
    margin-bottom: 2rem;
  }

  .contact-list {
    flex-direction: column;
    width: 100%;
  }
}

@media (max-width: 768px) {
  .contact-header {
    padding: 1rem;
  }

  .contact-header h1 {
    font-size: 2rem;
    text-align: center;
  }
  
  .contact-header p {
    width: 100%;
    text-align: center;
  }
}

@media (max-width: 600px) {
  .social-grid {
    grid-template-columns: 1fr;
  }
}
</style>
